import {
  ArrayField,
  AutocompleteInput,
  Button,
  Datagrid,
  FunctionField,
  List,
  ReferenceInput,
  SelectInput,
  SimpleList,
  SingleFieldList,
  TextField,
} from "react-admin";
import { Chip, Grid, useMediaQuery } from "@mui/material";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import { useNavigate } from "react-router-dom";

import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import { checkPermission } from "server/security/utils";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import ProfessorField from "server/components/Fields/ProfessorField";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import RoomField from "server/components/Fields/RoomField";
import { MobileListRow } from "features/MobileListRow";
import { settings } from "app";
import { PreparatoryClassTypeChoices } from "utils/choices";
import { CLASSES, MOBILE_BREAKPOINT, PREPARATORY_CLASSES, StudentServicePath } from "utils/constants";

const ListPreparatoryClasses = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  // Attendance is the only action here - equipment and grades are wired for regular teaching.
  // The attendance screen itself is class-type agnostic, so it is reused as-is; returnTo brings the
  // user back to this list instead of the regular classes one.
  const renderActionButtons = record => {
    if (!checkPermission(getPermissions(), permissions.canPreviewClassAttendance)) return null;

    return (
      <Grid sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
        <Button
          label="Присъствие"
          color="primary"
          startIcon={<CoPresentIcon />}
          onClick={() =>
            navigate(`/${StudentServicePath}${CLASSES}/attendance/${record.id}/edit`, {
              state: {
                studyGroups: record?.studyGroups,
                disciplineName: record?.disciplineName,
                classTypeId: record?.classTypeId,
                date: record?.date,
                startTime: record?.startTime,
                endTime: record?.endTime,
                returnTo: `/${StudentServicePath}${PREPARATORY_CLASSES}`,
              },
            })
          }
        />
      </Grid>
    );
  };

  const preparatoryClassesFilter = [
    <ReferenceInput
      key="academicYearId"
      source="academicYearId"
      reference={studentRequests.getAcademicYearsSelect()}
      alwaysOn
    >
      <SelectInput
        label="Академична година"
        optionText="name"
        optionValue="id"
        helperText={false}
      />
    </ReferenceInput>,
    <CustomDateInput
      key="fromDate"
      source="fromDate"
      label="От дата"
      alwaysOn
      noTime
    />,
    <CustomDateInput
      key="toDate"
      source="toDate"
      label="До дата"
      alwaysOn
      noTime
    />,
    <ReferenceInput
      key="disciplineId"
      source="disciplineId"
      reference={administrationRequests.getDisciplinesSelect()}
      label="Дисциплина"
      alwaysOn
    >
      <AutocompleteInput
        optionValue="id"
        optionText="name"
        label="Дисциплина"
        filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
        sx={{ width: "300px" }}
      />
    </ReferenceInput>,
    <SelectInput
      key="classTypeId"
      source="classTypeId"
      label="Вид занятие"
      choices={PreparatoryClassTypeChoices}
      alwaysOn
    />,
    <ProfessorInput
      key="professorEmployeeId"
      source="professorEmployeeId"
      alwaysOn
    />,
    <ReferenceInput
      key="roomId"
      source="roomId"
      reference={administrationRequests.getRoomsSelect()}
      alwaysOn
    >
      <AutocompleteInput
        label="Зала"
        optionValue="id"
        optionText={rec => `${rec.roomName} (${rec.roomNumber || ""}) ${rec.buildingName}`}
        filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
        sx={{ width: "300px" }}
      />
    </ReferenceInput>,
  ];

  const renderTime = record => `${record.startTime} - ${record.endTime}`;

  const renderClassType = record =>
    PreparatoryClassTypeChoices.find(c => c.id === record.classTypeId)?.name || "N/A";

  return (
    <List
      title="Занятия подготвителна година"
      filters={preparatoryClassesFilter}
      exporter={false}
    >
      {isMobile ? (
        <SimpleList
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "Дисциплина", value: record?.disciplineName }]} />
              <MobileListRow fields={[{ label: "Дата", value: record?.date }]} />
            </div>
          )}
          secondaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "Разписание", value: record?.preparatoryScheduleName }]} />
              <MobileListRow fields={[{ label: "Час", value: renderTime(record) }]} />
              <MobileListRow fields={[{ label: "Вид занятие", value: renderClassType(record) }]} />
              <MobileListRow
                fields={[{ label: "Учебни групи", value: record?.studyGroups?.join(", ") || "Всички" }]}
              />
              <MobileListRow fields={[{ label: "Зала", value: <RoomField /> }]} />
              <MobileListRow
                fields={[{ label: "Преподавател", value: <ProfessorField source="professorId" /> }]}
              />
              {renderActionButtons(record)}
            </div>
          )}
        />
      ) : (
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
        >
          <TextField
            source="preparatoryScheduleName"
            label="Разписание"
            sortable={false}
          />
          <TextField
            source="disciplineName"
            label="Дисциплина"
            sortable={false}
          />
          <TextField
            source="date"
            label="Дата"
            sortable={false}
          />
          <FunctionField
            label="Час"
            sortable={false}
            render={renderTime}
          />
          <FunctionField
            label="Вид занятие"
            sortable={false}
            render={renderClassType}
          />
          <ArrayField
            source="studyGroups"
            label="Учебни групи"
            sortable={false}
          >
            <SingleFieldList linkType={false}>
              <FunctionField
                render={rec => (
                  <Chip
                    key={rec}
                    label={rec}
                  />
                )}
              />
            </SingleFieldList>
          </ArrayField>
          <RoomField label="Зала" />
          <ProfessorField
            source="professorId"
            label="Преподавател"
          />
          <FunctionField render={renderActionButtons} />
        </Datagrid>
      )}
    </List>
  );
};

export default ListPreparatoryClasses;
